import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  GitBranch,
  Phone, 
  MessageCircle, 
  Mail, 
  Linkedin,
  Send,
  MessageSquareText,
  Calendar,
  ChevronDown, 
  ChevronRight,
  Plus,
  List,
  CheckCircle,
  Target,
  Settings,
  CreditCard
} from 'lucide-react';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['lead-gen']);

  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (paths: string[]) => paths.some(path => location.pathname === path);

  return (
    <div className="w-64 bg-white border-r border-gray-200 h-full overflow-y-auto">
      <div className="p-6">
        <div className="flex items-center space-x-2">
          <img src="/salespad logo.svg" alt="SalesPad" className="w-8 h-8" />
          <span className="text-xl font-bold text-gray-900">SalesPad</span>
        </div>
      </div>

      <nav className="px-4 pb-4">
        {/* Dashboard */}
        <Link
          to="/app/dashboard"
          className={`flex items-center space-x-3 px-3 py-2 rounded-lg mb-1 transition-colors ${
            isActive('/app/dashboard') 
              ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700' 
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          <LayoutDashboard size={20} />
          <span className="font-medium">Dashboard</span>
        </Link>

        {/* Pipeline */}
        <Link
          to="/app/pipeline"
          className={`flex items-center space-x-3 px-3 py-2 rounded-lg mb-1 transition-colors ${
            isActive('/app/pipeline') 
              ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700' 
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          <GitBranch size={20} />
          <span className="font-medium">Pipeline</span>
        </Link>

        {/* Lead Gen Agent */}
        <div className="mb-1">
          <button
            onClick={() => toggleMenu('lead-gen')}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
              isParentActive(['/app/lead-gen/place-request', '/app/lead-gen/requests', '/app/lead-gen/pre-qualified', '/app/lead-gen/qualified'])
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center space-x-3">
              <Users size={20} />
              <span className="font-medium">Lead Gen Agent</span>
            </div>
            {expandedMenus.includes('lead-gen') ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          
          {expandedMenus.includes('lead-gen') && (
            <div className="ml-6 mt-1 space-y-1">
              <Link
                to="/app/lead-gen/place-request"
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive('/app/lead-gen/place-request')
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Plus size={16} />
                <span>Place Request</span>
              </Link>
              <Link
                to="/app/lead-gen/requests"
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive('/app/lead-gen/requests')
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <List size={16} />
                <span>Leads Requests</span>
              </Link>
              <Link
                to="/app/lead-gen/pre-qualified"
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive('/app/lead-gen/pre-qualified')
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <CheckCircle size={16} />
                <span>Pre-qualified Leads</span>
              </Link>
              <Link
                to="/app/lead-gen/qualified"
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive('/app/lead-gen/qualified')
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Target size={16} />
                <span>Qualified Ready Leads</span>
              </Link>
            </div>
          )}
        </div>

        {/* Call Agent */}
        <Link
          to="/app/call-agent"
          className={`flex items-center space-x-3 px-3 py-2 rounded-lg mb-1 transition-colors ${
            isActive('/app/call-agent')
              ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Phone size={20} />
          <span className="font-medium">Call Agent</span>
        </Link>

        {/* WhatsApp Agent */}
        <Link
          to="/app/whatsapp-agent"
          className={`flex items-center space-x-3 px-3 py-2 rounded-lg mb-1 transition-colors ${
            isActive('/app/whatsapp-agent')
              ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          <MessageCircle size={20} />
          <span className="font-medium">WhatsApp Agent</span>
        </Link>

        {/* Email Agent */}
        <Link
          to="/app/email-agent"
          className={`flex items-center space-x-3 px-3 py-2 rounded-lg mb-1 transition-colors ${
            isActive('/app/email-agent')
              ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Mail size={20} />
          <span className="font-medium">Email Agent</span>
        </Link>

        {/* LinkedIn Agent */}
        <div className="mb-1">
          <button
            onClick={() => toggleMenu('linkedin-agent')}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
              isParentActive(['/app/linkedin-agent/outreach', '/app/linkedin-agent/post-comment'])
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center space-x-3">
              <Linkedin size={20} />
              <span className="font-medium">LinkedIn Agent</span>
            </div>
            {expandedMenus.includes('linkedin-agent') ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          
          {expandedMenus.includes('linkedin-agent') && (
            <div className="ml-6 mt-1 space-y-1">
              <Link
                to="/app/linkedin-agent/outreach"
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive('/app/linkedin-agent/outreach')
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Send size={16} />
                <span>Outreach</span>
              </Link>
              <Link
                to="/app/linkedin-agent/post-comment"
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive('/app/linkedin-agent/post-comment')
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <MessageSquareText size={16} />
                <span>Post & Comment</span>
              </Link>
            </div>
          )}
        </div>
        {/* Meetings */}
        <Link
          to="/app/meetings"
          className={`flex items-center space-x-3 px-3 py-2 rounded-lg mb-2 transition-colors ${
            isActive('/app/meetings')
              ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Calendar size={20} />
          <span className="font-medium">Meetings</span>
        </Link>
      </nav>
      
      {/* Credits Section */}
      <div className="px-4 pb-4">
        <div className="mb-2 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Credits</span>
            <span className="text-sm text-gray-600">700/1000</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
            <div className="bg-blue-500 h-2 rounded-full w-[70%]"></div>
          </div>
          <button className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
            <CreditCard size={16} />
            <span>Top up credits</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;